import React, { useState, useEffect, useCallback } from 'react'
import { useToast, Box, Button, Heading, Text, Input, Textarea, Badge, Tabs, TabList, Tab, TabPanels, TabPanel, Table, Thead, Tbody, Th, Tr, Td } from '@chakra-ui/react'
import { Link, useNavigate } from 'react-router-dom'
import SimplePopover from '../includes/SimplePopover'
import moment from 'moment'
import api from '../../api'
import useStore from '../../store'
import utils from '../../utils/utils'

const JOBS = [
  'email_stats',
  'send_stage_reminders',
  'send_scheduled_automatic_messages',
  'check_participants_behind_schedule',
  'daily_trial_update_email',
  'weekly_trial_update_email',
  'sms_status_update',
  'remove_invalid_push_tokens'
]

export default function Root () {
  const [newTeamName, setNewTeamName] = useState('')
  const [newTeamCode, setNewTeamCode] = useState('')
  const [teams, setTeams] = useState([])
  const [users, setUsers] = useState([])
  const [teamQuery, setTeamQuery] = useState('')
  const [contacts, setContacts] = useState([])
  const [selectedContact, setSelectedContact] = useState('')
  const [contactQuery, setContactQuery] = useState('')
  const [messages, setMessages] = useState([])
  const [newMessage, setNewMessage] = useState('')
  const toast = useToast()
  const navigate = useNavigate()
  const { user, updateUser } = useStore()

  useEffect(() => {
    if (!user) return
    api.root.getTeams(({ teams }) => setTeams(teams))
    api.root.getUsers(({ users }) => setUsers(users))
  }, [user])

  useEffect(() => {
    if (selectedContact && user) {
      api.root.getMessages(selectedContact, ({ messages }) => setMessages(messages))
    }
  }, [user, selectedContact])

  useEffect(() => {
    if (!user) return
    fetchContacts(contactQuery)
  }, [contactQuery, user])

  const fetchContacts = useCallback(utils.debounce((contactQuery) => {
    api.root.getContacts(contactQuery, ({ contacts }) => {
      setContacts(contacts)
    })
  }, 300), [])

  function sendMessage () {
    api.root.sendMessage(selectedContact, { body: newMessage }, () => {
      setNewMessage('')
      api.root.getMessages(selectedContact, ({ messages }) => setMessages(messages))
    }, err => toast({ title: 'Unable to send message', description: err.message, status: 'error' }))
  }

  function createTeam () {
    api.teams.create({ code: newTeamCode, name: newTeamName }, newTeam => {
      const newTeams = Object.assign([], teams)
      newTeams.push(newTeam)
      setTeams(newTeams)
      navigate(`/root/teams/${newTeam._id}/edit`)
    }, err => toast({ title: 'Unable to create team', description: err.message, status: 'error' }))
  }
  function switchTeam (teamId) {
    api.users.updateTeam(user._id, teamId, () => {
      updateUser(user._id, { team: teamId })
    }, err => toast({ title: 'Unable to switch team', description: err.message, status: 'error' }))
  }

  function testSentry () {
    const x = {}
    console.log(x.y.z)
  }

  function runJob (job) {
    api.root.runScheduledJob(job, () => {
      toast({ title: 'Job has been run', status: 'success' })
    }, err => toast({ title: 'Unable to run job', description: err.message, status: 'error' }))
  }

  const filteredTeams = teamQuery ? teams.filter(t => t.name.toLowerCase().includes(teamQuery.toLowerCase())) : teams

  return (
    <Box>
      <Tabs>
        <TabList>
          <Tab>Teams</Tab>
          <Tab>Users</Tab>
          <Tab>Comms</Tab>
          <Tab>Settings</Tab>
        </TabList>

        <TabPanels pt={5}>
          <TabPanel>
            <Box display='flex' alignItems='center' mb={4} justifyContent='space-between'>
              <Box display='flex' alignItems='center'>
                <Heading as='h3' size='md'>Existing teams</Heading>
                <Input size='sm' w='150px' ml={2} placeholder='Search teams' value={teamQuery} onChange={e => setTeamQuery(e.target.value)} autoFocus />
              </Box>
              <SimplePopover
                trigger={<Button colorScheme='primary'>Create team</Button>}
                header='Create new team'
                body={
                  <>
                    <Input mt={5} placeholder='Team code' value={newTeamCode} onChange={e => setNewTeamCode(e.target.value)} />
                    <Input mt={3} placeholder='Team name' value={newTeamName} onChange={e => setNewTeamName(e.target.value)} />
                    <Button mt={3} colorScheme='primary' onClick={createTeam}>Create team</Button>
                  </>
                }
              />
            </Box>
            <Table size='sm'>
              <Thead>
                <Tr>
                  <Th>Team name</Th>
                  <Th>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {filteredTeams.map(t =>
                  <Tr key={t._id}>
                    <Td>{t.name}</Td>
                    <Td collapsing>{user.team !== t._id
                      ? <Button size='xs' colorScheme='blue' onClick={e => switchTeam(t._id)}>Switch</Button>
                      : <Badge colorScheme='green'>You're in this team</Badge>}
                      <Button ml={2} size='xs' as={Link} to={`/root/teams/${t._id}/edit`}>Edit</Button>
                    </Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
          </TabPanel>

          <TabPanel>
            <Table size='sm'>
              <Thead>
                <Tr>
                  <Th>Email</Th>
                  <Th>Name</Th>
                  <Th>Team</Th>
                  <Th>Last seen</Th>
                </Tr>
              </Thead>
              <Tbody>
                {users.map(u =>
                  <Tr key={u._id}>
                    <Td>{u.email}</Td>
                    <Td>{u.firstName} {u.lastName}</Td>
                    <Td>
                      {u.ownerTeam?.name}
                      {u.ownerTeam?._id !== user.team && <Button size='xs' ml={2} colorScheme='blue' onClick={e => switchTeam(u.ownerTeam._id)}>Switch</Button>}
                    </Td>
                    <Td>{u.lastSeenAt ? moment(u.lastSeenAt).fromNow() : <Badge>Never</Badge>}</Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
          </TabPanel>

          <TabPanel>
            <Box display={{ md: 'flex' }} alignItems='top'>
              <Box>
                <Heading as='h3' size='md'>Contacts</Heading>
                <Input size='sm' w='300px' mt={2} placeholder='Search contacts' value={contactQuery} onChange={e => setContactQuery(e.target.value)} />
                <Table size='xs' w='300px' mt={2}>
                  <Tbody>
                    {contacts.map(c =>
                      <Tr key={c}>
                        <Td><Text cursor='pointer' fontSize='xs' onClick={() => setSelectedContact(c)}>{c}</Text></Td>
                      </Tr>
                    )}
                  </Tbody>
                </Table>
              </Box>

              {selectedContact &&
                <Box flex={1} ml={2}>
                  <Heading textAlign='center' as='h3' size='sm'>{selectedContact}</Heading>
                  <Box h='500px' overflowY='scroll'>
                    <Box display='flex' justifyContent='center' alignItems='center' mt={2}>
                      <Textarea mt={2} size='xs' placeholder='Type a message' value={newMessage} onChange={e => setNewMessage(e.target.value)} />
                      <Button ml={2} colorScheme='primary' onClick={sendMessage}>Send</Button>
                    </Box>
                    {messages?.map(m =>
                      <Box key={m._id} display='flex' justifyContent={m.inbound ? 'start' : 'end'} mt={5}>
                        <Box p={1} borderRadius='md' mb={2} bg={m.inbound ? 'gray.100' : 'primary.100'} w='80%'>
                          <Text fontSize='xs' fontWeight='bold'>{moment(m.createdAt).format('LLLL')}</Text>
                          <Text fontSize='sm' whiteSpace='pre-line'>{m.body}</Text>
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Box>}
            </Box>
          </TabPanel>

          <TabPanel>
            <Button onClick={testSentry}>Test Sentry</Button>

            <Heading mt={4} size='sm'>Run scheduled jobs</Heading>
            <p>Note that this is for testing only. Some of these jobs send comms out to users and participants.</p>
            <Box display='flex-wrap'>
              {JOBS.map(job =>
                <Button key={job} size='sm' mr={2} mb={2} onClick={e => runJob(job)}>{job}</Button>
              )}
            </Box>
          </TabPanel>
        </TabPanels>
      </Tabs>

    </Box>
  )
}
